import { getFilenameParts } from '..';

interface GenerateStatementFileKeyForS3Input {
    baseNameFallback: string;
    file: File;
    userId: string;
}

/**
 * Given a File object and a userId, generate a file key for use in S3, using the original file name
 * and extension and adding a timestamp. Any spaces in the original filename are replaced with
 * underscores. The key will use the userId as parent folder for the .csv file.
 *
 * Example:
 * input:
 *  {
 *      file: { // this would actually be a File object with more properties
 *          name: 'boomdit Q1 2014.csv',
 *          // etc...
 *      },
 *      userId: '1622578423XX0000000000000X',
 * }
 * output: '1622578423XX0000000000000X/boomdit Q1 2014.1623353309541.csv`
 *
 * If an extension cannot be read from the filename, `.csv` is used by default.
 *
 * Note that spaces are later replaced with underscores by packages/utils/src/s3/sanitizeS3Key.ts
 * @param file - the File object to be uploaded
 * @returns the key to be used when uploading to S3
 */
export const generateCSVFileKeyForS3 = ({
    baseNameFallback,
    file,
    userId,
}: GenerateStatementFileKeyForS3Input): string => {
    const { baseName, extension } = getFilenameParts(file.name);
    const baseNameFinal = baseName === null ? baseNameFallback : baseName;

    const filename =
        extension !== null
            ? `${baseNameFinal}.${Date.now()}.${extension}`
            : `${baseNameFinal}.${Date.now()}.csv`;

    return `${userId}/${filename}`;
};
